// Benefit limits and excess for insurer schemes (docs/plans/phase2-private-insurance.md
// §3, §5.1). The split rule has already divided a charge between the insurer and the
// patient (co-pay lives there). These rules then move part of the insurer's share to the
// patient: an excess the patient pays first, and limits the insurer won't pay beyond.
//
// The invariant is the split rule's: payer + patient = total, exactly. Money only ever
// moves from the insurer to the patient here, never back, and every move is reported so
// the charge can say why the patient pays more.

import type { ServiceItemCategory } from '../catalogue.ts';
import type { Money } from '../money.ts';
import { addMoney, maxMoney, minMoney, subtractMoney, zeroMoney } from '../money.ts';

export const BENEFIT_RULE_KINDS = ['limit', 'excess', 'preauth_required'] as const;
export type BenefitRuleKind = (typeof BENEFIT_RULE_KINDS)[number];

export const BENEFIT_PERIODS = ['per_visit', 'per_year'] as const;
export type BenefitPeriod = (typeof BENEFIT_PERIODS)[number];

export interface BenefitRule {
  readonly id: string;
  readonly kind: BenefitRuleKind;
  /** Null only for `preauth_required`. */
  readonly period: BenefitPeriod | null;
  /** Null: every category. */
  readonly category: ServiceItemCategory | null;
  /** The limit, the excess, or the pre-auth threshold. */
  readonly amount: Money;
  readonly status: 'active' | 'inactive';
}

/** Active rules of one kind that apply to a category (their own or every category). */
export function selectBenefitRules(
  rules: readonly BenefitRule[],
  q: { readonly kind: BenefitRuleKind; readonly category: ServiceItemCategory },
): BenefitRule[] {
  return rules.filter(
    (r) =>
      r.status === 'active' &&
      r.kind === q.kind &&
      (r.category === null || r.category === q.category),
  );
}

export interface BenefitApplication {
  readonly ruleId: string;
  readonly kind: 'limit' | 'excess';
  /** Moved from the insurer's share to the patient's; always more than zero. */
  readonly shifted: Money;
}

export interface ApplyBenefitsInput {
  readonly total: Money;
  readonly payerAmount: Money;
  readonly patientAmount: Money;
  readonly category: ServiceItemCategory;
  readonly rules: readonly BenefitRule[];
  /**
   * Per rule ID, what is already used in the rule's period: for a limit, the insurer's share
   * of the cover's other charges; for an excess, what the patient has already paid of it.
   * A missing entry means nothing used.
   */
  readonly usage: ReadonlyMap<string, Money>;
}

export interface BenefitResult {
  readonly payerAmount: Money;
  readonly patientAmount: Money;
  readonly applications: readonly BenefitApplication[];
}

/** Rules apply in a stable order (by ID) so a charge is always adjusted the same way. */
const byId = (a: BenefitRule, b: BenefitRule) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Applies the excess first (the patient pays the insurer's share until the excess is
 * met), then every limit to what is left of the insurer's share (plan B5: after co-pay).
 */
export function applyBenefits(input: ApplyBenefitsInput): BenefitResult {
  const { total, payerAmount, patientAmount } = input;
  if (addMoney(payerAmount, patientAmount).cents !== total.cents)
    throw new RangeError('The payer and patient shares must add up to the total');
  if (payerAmount.cents < 0n || patientAmount.cents < 0n)
    throw new RangeError('Benefits apply to charges, not to credits');

  let payer = payerAmount;
  const applications: BenefitApplication[] = [];
  const used = (id: string) => input.usage.get(id) ?? zeroMoney(total.currency);

  const move = (rule: BenefitRule, kind: 'limit' | 'excess', amount: Money) => {
    if (amount.cents <= 0n) return;
    payer = subtractMoney(payer, amount);
    applications.push({ ruleId: rule.id, kind, shifted: amount });
  };

  for (const rule of selectBenefitRules(input.rules, {
    kind: 'excess',
    category: input.category,
  }).sort(byId)) {
    const remaining = maxMoney(
      subtractMoney(rule.amount, used(rule.id)),
      zeroMoney(total.currency),
    );
    move(rule, 'excess', minMoney(remaining, payer));
  }
  for (const rule of selectBenefitRules(input.rules, {
    kind: 'limit',
    category: input.category,
  }).sort(byId)) {
    const remaining = maxMoney(
      subtractMoney(rule.amount, used(rule.id)),
      zeroMoney(total.currency),
    );
    move(rule, 'limit', maxMoney(subtractMoney(payer, remaining), zeroMoney(total.currency)));
  }

  return { payerAmount: payer, patientAmount: subtractMoney(total, payer), applications };
}

/** Words for a rule, for Settings and the bill ("Outpatient limit per year: KES 50,000"). */
export const BENEFIT_KIND_WORDS: Record<BenefitRuleKind, string> = {
  limit: 'Limit',
  excess: 'Excess',
  preauth_required: 'Needs the insurer’s approval',
};

/** Category words for rule sentences, lower case as they sit mid-sentence. */
export const BENEFIT_CATEGORY_WORDS: Record<ServiceItemCategory, string> = {
  consultation: 'consultations',
  procedure: 'procedures',
  lab_test: 'lab tests',
  imaging: 'imaging',
  medicine: 'medicines',
  consumable: 'consumables',
  bed_day: 'bed-days',
  mortuary: 'mortuary charges',
  nursing: 'nursing',
  other: 'other services',
};

export const BENEFIT_PERIOD_WORDS: Record<BenefitPeriod, string> = {
  per_visit: 'per visit',
  per_year: 'per benefit year',
};
